// Extracted Camps Component
// Main interface for viewing and managing extracted camp email data

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Search, 
  Filter, 
  SortAsc, 
  SortDesc, 
  RefreshCw, 
  Calendar,
  MapPin,
  DollarSign,
  User,
  AlertTriangle,
  Heart,
  Bookmark,
  EyeOff,
  MoreHorizontal,
  Plus,
  CheckSquare,
  Square
} from 'lucide-react';
import Icon from '@/components/ui/Icon';
import CampEmailCard from './CampEmailCard';
import CampDetailsModal from './CampDetailsModal';
import ManualReviewInterface from './ManualReviewInterface';
import { 
  getCampEmails, 
  getCampEmailStats, 
  searchCampEmails,
  bulkUpdateCampStatus 
} from '@/services/campEmailService';

/**
 * Extracted Camps Component
 * Main interface for viewing and managing extracted camp email data
 */
const ExtractedCamps = ({ user }) => {
  const [campEmails, setCampEmails] = useState([]);
  const [filteredEmails, setFilteredEmails] = useState([]);
  const [stats, setStats] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter and search state
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('date_desc');
  const [showNeedsReview, setShowNeedsReview] = useState(false);
  
  // Selection state
  const [selectedEmails, setSelectedEmails] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  
  // Modal state
  const [selectedCampEmail, setSelectedCampEmail] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showManualReview, setShowManualReview] = useState(false);

  /**
   * Load camp emails and statistics
   */
  const loadCampData = useCallback(async () => {
    if (!user?.id) return;

    try {
      setIsLoading(true);
      setError(null);

      // Load camp emails
      const filters = {
        sortBy: sortBy === 'date_desc' ? 'email_date' : 
                sortBy === 'date_asc' ? 'email_date' : 
                sortBy === 'camp_date_asc' ? 'camp_dates_start' : 'extraction_confidence',
        sortOrder: sortBy === 'date_asc' || sortBy === 'camp_date_asc' ? 'asc' : 'desc'
      };

      const emails = await getCampEmails(user.id, filters);
      setCampEmails(emails);

      // Load statistics
      const emailStats = await getCampEmailStats(user.id);
      setStats(emailStats);

    } catch (err) {
      console.error('Error loading camp data:', err);
      setError('Failed to load camp data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, sortBy]);

  /**
   * Apply filters and search
   */
  const applyFilters = useCallback(() => {
    let filtered = [...campEmails];

    // Apply status filter
    if (activeFilter !== 'all') {
      if (activeFilter === 'needs_review') {
        filtered = filtered.filter(email => email.needs_manual_review);
      } else {
        filtered = filtered.filter(email => email.user_status === activeFilter);
      }
    }

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(email => 
        email.camp_name?.toLowerCase().includes(term) ||
        email.camp_organization?.toLowerCase().includes(term) ||
        email.camp_location?.toLowerCase().includes(term) ||
        email.email_subject?.toLowerCase().includes(term)
      );
    }

    setFilteredEmails(filtered);
  }, [campEmails, activeFilter, searchTerm]);

  /**
   * Handle search
   */
  const handleSearch = useCallback(async (term) => {
    setSearchTerm(term);
    
    if (term.trim()) {
      try {
        const results = await searchCampEmails(user.id, term);
        setFilteredEmails(results);
      } catch (err) {
        console.error('Search error:', err);
        setError('Search failed. Please try again.');
      }
    } else {
      applyFilters();
    }
  }, [user?.id, applyFilters]);

  /**
   * Handle status change for a single camp
   */
  const handleStatusChange = useCallback(async (campEmailId, status, notes) => {
    try {
      // Update in local state
      setCampEmails(prev => 
        prev.map(email => 
          email.id === campEmailId 
            ? { ...email, user_status: status, user_notes: notes || email.user_notes }
            : email
        )
      );

      // Update filtered emails
      setFilteredEmails(prev => 
        prev.map(email => 
          email.id === campEmailId 
            ? { ...email, user_status: status, user_notes: notes || email.user_notes }
            : email
        )
      );

      // Reload stats
      const emailStats = await getCampEmailStats(user.id);
      setStats(emailStats);

    } catch (err) {
      console.error('Error updating camp status:', err);
      setError('Failed to update camp status. Please try again.');
    }
  }, [user?.id]);

  /**
   * Handle bulk status change
   */
  const handleBulkStatusChange = useCallback(async (status) => {
    if (selectedEmails.size === 0) return;

    try {
      const emailIds = Array.from(selectedEmails);
      await bulkUpdateCampStatus(emailIds, status);

      // Update local state
      setCampEmails(prev => 
        prev.map(email => 
          selectedEmails.has(email.id) 
            ? { ...email, user_status: status }
            : email
        )
      );

      setFilteredEmails(prev => 
        prev.map(email => 
          selectedEmails.has(email.id) 
            ? { ...email, user_status: status }
            : email
        )
      );

      // Clear selection
      setSelectedEmails(new Set());
      setSelectAll(false);

      // Reload stats
      const emailStats = await getCampEmailStats(user.id);
      setStats(emailStats);

    } catch (err) {
      console.error('Error bulk updating camp status:', err);
      setError('Failed to update selected camps. Please try again.');
    }
  }, [selectedEmails]);

  /**
   * Handle select all
   */
  const handleSelectAll = useCallback(() => {
    if (selectAll) {
      setSelectedEmails(new Set());
      setSelectAll(false);
    } else {
      const allIds = new Set(filteredEmails.map(email => email.id));
      setSelectedEmails(allIds);
      setSelectAll(true);
    }
  }, [selectAll, filteredEmails]);

  /**
   * Handle individual selection
   */
  const handleSelectEmail = useCallback((campEmailId, selected) => {
    const newSelected = new Set(selectedEmails);
    if (selected) {
      newSelected.add(campEmailId);
    } else {
      newSelected.delete(campEmailId);
    }
    setSelectedEmails(newSelected);
    setSelectAll(newSelected.size === filteredEmails.length);
  }, [selectedEmails, filteredEmails.length]);

  /**
   * Handle view camp details
   */
  const handleViewCamp = useCallback((campEmail) => {
    setSelectedCampEmail(campEmail);
    setShowDetailsModal(true);
  }, []);

  /**
   * Handle manual review
   */
  const handleManualReview = useCallback((campEmail) => {
    setSelectedCampEmail(campEmail);
    setShowManualReview(true);
  }, []);

  // Load data on mount and when dependencies change
  useEffect(() => {
    loadCampData();
  }, [loadCampData]);

  // Apply filters when data or filters change
  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  // Get filter counts
  const getFilterCount = (filter) => {
    switch (filter) {
      case 'all': return stats.total || 0;
      case 'new': return stats.new || 0;
      case 'favorited': return stats.favorited || 0;
      case 'needs_review': return stats.needsReview || 0;
      case 'hidden': return stats.hidden || 0;
      case 'saved_for_later': return stats.savedForLater || 0;
      default: return 0;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <Icon name="loader-circle" size="xl" className="animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading camp emails...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="extracted-camps space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Camp Emails</h1>
          <p className="text-gray-600">
            {stats.total || 0} camp emails found from your Gmail
          </p>
        </div>
        <Button 
          onClick={loadCampData}
          variant="outline"
          size="sm"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            <p className="text-red-800">{error}</p>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <Card>
        <CardContent className="p-6">
          <div className="space-y-4">
            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search camps, organizations, or locations..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Filter Tabs */}
            <Tabs value={activeFilter} onValueChange={setActiveFilter}>
              <TabsList className="grid w-full grid-cols-6">
                <TabsTrigger value="all">
                  All ({getFilterCount('all')})
                </TabsTrigger>
                <TabsTrigger value="new">
                  New ({getFilterCount('new')})
                </TabsTrigger>
                <TabsTrigger value="favorited">
                  Favorites ({getFilterCount('favorited')})
                </TabsTrigger>
                <TabsTrigger value="needs_review">
                  Review ({getFilterCount('needs_review')})
                </TabsTrigger>
                <TabsTrigger value="saved_for_later">
                  Saved ({getFilterCount('saved_for_later')})
                </TabsTrigger>
                <TabsTrigger value="hidden">
                  Hidden ({getFilterCount('hidden')})
                </TabsTrigger>
              </TabsList>
            </Tabs>

            {/* Sort and Bulk Actions */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-700">Sort by:</label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm"
                  >
                    <option value="date_desc">Newest First</option>
                    <option value="date_asc">Oldest First</option>
                    <option value="camp_date_asc">Camp Date (Earliest)</option>
                    <option value="confidence_desc">Highest Confidence</option>
                  </select>
                </div>
              </div>

              {/* Bulk Actions */}
              {selectedEmails.size > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">
                    {selectedEmails.size} selected
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleBulkStatusChange('favorited')}
                  >
                    <Heart className="h-4 w-4 mr-1" />
                    Favorite
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleBulkStatusChange('saved_for_later')}
                  >
                    <Bookmark className="h-4 w-4 mr-1" />
                    Save
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleBulkStatusChange('hidden')}
                  >
                    <EyeOff className="h-4 w-4 mr-1" />
                    Hide
                  </Button>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Camp Emails List */}
      <div className="space-y-4">
        {filteredEmails.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <Calendar className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {searchTerm ? 'No camps found' : 'No camp emails found yet'}
              </h3>
              <p className="text-gray-600 mb-4">
                {searchTerm 
                  ? 'Try adjusting your search terms or filters.'
                  : 'We haven\'t found any camp-related emails in your account yet.'
                }
              </p>
              {!searchTerm && (
                <Button onClick={loadCampData}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Re-scan My Emails
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Select All */}
            <div className="flex items-center gap-2 p-2">
              <button
                onClick={handleSelectAll}
                className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
              >
                {selectAll ? (
                  <CheckSquare className="h-4 w-4" />
                ) : (
                  <Square className="h-4 w-4" />
                )}
                Select All
              </button>
            </div>

            {/* Camp Email Cards */}
            <div className="grid grid-cols-1 gap-4">
              {filteredEmails.map((campEmail) => (
                <CampEmailCard
                  key={campEmail.id}
                  campEmail={campEmail}
                  isSelected={selectedEmails.has(campEmail.id)}
                  onStatusChange={handleStatusChange}
                  onViewCamp={handleViewCamp}
                  onManualReview={handleManualReview}
                  onSelect={(selected) => handleSelectEmail(campEmail.id, selected)}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Modals */}
      {showDetailsModal && selectedCampEmail && (
        <CampDetailsModal
          campEmail={selectedCampEmail}
          isOpen={showDetailsModal}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedCampEmail(null);
          }}
          onStatusChange={handleStatusChange}
        />
      )}

      {showManualReview && selectedCampEmail && (
        <ManualReviewInterface
          campEmail={selectedCampEmail}
          isOpen={showManualReview}
          onClose={() => {
            setShowManualReview(false);
            setSelectedCampEmail(null);
          }}
          onStatusChange={handleStatusChange}
        />
      )}
    </div>
  );
};

export default ExtractedCamps;
